import { useMemo, useState } from "react";
import { cn } from "@/utils/utils";

export type TFieldDiffStatus = "added" | "removed" | "changed" | "unchanged";

export type TFieldDiff = {
    key: string;
    status: TFieldDiffStatus;
    oldValue: unknown;
    newValue: unknown;
};

export type TDiffLineKind = "add" | "remove" | "context";

export type TDiffLine = { kind: TDiffLineKind; text: string };

type TAuditData = Record<string, unknown> | null;

/** Above this many cells the LCS table gets too big; fall back to whole-value remove/add. */
const MAX_LCS_CELLS = 250_000;

const isContainer = (value: unknown): value is object =>
    typeof value === "object" && value !== null;

/** JSON with object keys sorted, so key order never registers as a change. */
const stableStringify = (value: unknown): string =>
    JSON.stringify(value, (_key, v: unknown) =>
        isContainer(v) && !Array.isArray(v)
            ? Object.fromEntries(Object.entries(v).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
            : v,
    ) ?? "undefined";

/** Pretty-printed value lines, minus JSON's trailing commas (noise in a line diff). */
const formatValueLines = (value: unknown): string[] =>
    (JSON.stringify(value, null, 2) ?? "undefined")
        .split("\n")
        .map((line) => line.replace(/,$/, ""));

/** Per-field comparison of an audit row's old and new state, in old-row column order. */
export const diffAuditData = (oldData: TAuditData, newData: TAuditData): TFieldDiff[] => {
    const before = oldData ?? {};
    const after = newData ?? {};
    const keys = [...Object.keys(before), ...Object.keys(after).filter((k) => !(k in before))];
    return keys.map((key) => {
        const oldValue = before[key];
        const newValue = after[key];
        let status: TFieldDiffStatus;
        if (!(key in after)) status = "removed";
        else if (!(key in before)) status = "added";
        else if (stableStringify(oldValue) === stableStringify(newValue)) status = "unchanged";
        else status = "changed";
        return { key, status, oldValue, newValue };
    });
};

type TIndexedLine = TDiffLine & { aIndex: number; bIndex: number };

/** Line diff via longest common subsequence; each line keeps its index in its source. */
const diffLines = (a: string[], b: string[]): TIndexedLine[] => {
    if (a.length * b.length > MAX_LCS_CELLS) {
        return [
            ...a.map((text, i): TIndexedLine => ({ kind: "remove", text, aIndex: i, bIndex: -1 })),
            ...b.map((text, j): TIndexedLine => ({ kind: "add", text, aIndex: -1, bIndex: j })),
        ];
    }
    // lcs[i * width + j] = length of the LCS of a[i..] and b[j..]
    const width = b.length + 1;
    const lcs = new Uint32Array((a.length + 1) * width);
    const at = (i: number, j: number): number => lcs[i * width + j] ?? 0;
    for (let i = a.length - 1; i >= 0; i--) {
        for (let j = b.length - 1; j >= 0; j--) {
            lcs[i * width + j] =
                a[i] === b[j] ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
        }
    }
    const out: TIndexedLine[] = [];
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
        const aLine = a[i];
        const bLine = b[j];
        if (aLine !== undefined && aLine === bLine) {
            out.push({ kind: "context", text: aLine, aIndex: i++, bIndex: j++ });
        } else if (aLine !== undefined && (bLine === undefined || at(i + 1, j) >= at(i, j + 1))) {
            out.push({ kind: "remove", text: aLine, aIndex: i++, bIndex: -1 });
        } else if (bLine !== undefined) {
            out.push({ kind: "add", text: bLine, aIndex: -1, bIndex: j++ });
        }
    }
    return out;
};

const withKey = (key: string, lines: string[], kind: TDiffLineKind): TDiffLine[] =>
    lines.map((text, i) => ({ kind, text: i === 0 ? `${key}: ${text}` : text }));

/**
 * Display lines for one field. Changed objects/arrays get a nested line diff so only
 * the inner lines that differ are coloured; scalars show the old line then the new one.
 */
export const fieldDiffLines = (field: TFieldDiff): TDiffLine[] => {
    const { key, status, oldValue, newValue } = field;
    if (status === "unchanged") return withKey(key, formatValueLines(newValue), "context");
    if (status === "added") return withKey(key, formatValueLines(newValue), "add");
    if (status === "removed") return withKey(key, formatValueLines(oldValue), "remove");
    if (isContainer(oldValue) && isContainer(newValue)) {
        return diffLines(formatValueLines(oldValue), formatValueLines(newValue)).map((line) => ({
            kind: line.kind,
            text: line.aIndex === 0 || line.bIndex === 0 ? `${key}: ${line.text}` : line.text,
        }));
    }
    return [
        ...withKey(key, formatValueLines(oldValue), "remove"),
        ...withKey(key, formatValueLines(newValue), "add"),
    ];
};

const LINE_STYLES: Record<TDiffLineKind, { row: string; sign: string }> = {
    add: {
        row: "bg-green-50 text-green-900 dark:bg-green-950/60 dark:text-green-200",
        sign: "+",
    },
    remove: {
        row: "bg-red-50 text-red-900 dark:bg-red-950/60 dark:text-red-200",
        sign: "-",
    },
    context: { row: "text-foreground/80", sign: " " },
};

const DiffLines = ({ lines, testId }: { lines: TDiffLine[]; testId: string }) => (
    <pre
        data-testid={testId}
        className="text-xs font-mono bg-muted/40 py-2 rounded border overflow-auto max-h-80"
    >
        {lines.map((line, i) => (
            <div
                key={i}
                data-diff={line.kind}
                className={cn(
                    "flex px-2 whitespace-pre-wrap break-all",
                    LINE_STYLES[line.kind].row,
                )}
            >
                <span aria-hidden className="select-none w-4 shrink-0 opacity-60">
                    {LINE_STYLES[line.kind].sign}
                </span>
                <span className="min-w-0">{line.text}</span>
            </div>
        ))}
    </pre>
);

type TViewMode = "changes" | "full";

const ModeButton = ({
    active,
    onClick,
    children,
}: {
    active: boolean;
    onClick: () => void;
    children: string;
}) => (
    <button
        type="button"
        aria-pressed={active}
        onClick={onClick}
        className={cn(
            "px-2.5 py-1 text-xs rounded-md transition-colors",
            active
                ? "bg-background text-foreground font-medium shadow-sm"
                : "text-muted-foreground hover:text-foreground",
        )}
    >
        {children}
    </button>
);

/**
 * Coloured diff of an audit row's old/new state. Shows only the fields that differ by
 * default; "Full before/after" shows both complete states with the differences highlighted.
 */
export const AuditDataDiff = ({
    oldData,
    newData,
}: {
    oldData: TAuditData;
    newData: TAuditData;
}) => {
    const [mode, setMode] = useState<TViewMode>("changes");

    const fields = useMemo(() => diffAuditData(oldData, newData), [oldData, newData]);
    const linesByField = useMemo(() => fields.map(fieldDiffLines), [fields]);

    if (!oldData && !newData) return null;

    const changedCount = fields.filter((f) => f.status !== "unchanged").length;
    const unchangedCount = fields.length - changedCount;
    // INSERT / DELETE carry a single state, which the changes view already shows in full.
    const hasBothStates = oldData !== null && newData !== null;
    const showFull = hasBothStates && mode === "full";

    const changeLines = linesByField.filter((_, i) => fields[i]?.status !== "unchanged").flat();
    const allLines = linesByField.flat();

    return (
        <div>
            <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-xs font-semibold text-muted-foreground">
                    {changedCount === 1 ? "1 field changed" : `${changedCount} fields changed`}
                    {hasBothStates && !showFull && unchangedCount > 0 && (
                        <span className="font-normal">{` · ${unchangedCount} unchanged hidden`}</span>
                    )}
                </p>
                {hasBothStates && (
                    <div className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5">
                        <ModeButton active={!showFull} onClick={() => setMode("changes")}>
                            Changes only
                        </ModeButton>
                        <ModeButton active={showFull} onClick={() => setMode("full")}>
                            Full before/after
                        </ModeButton>
                    </div>
                )}
            </div>

            {showFull ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Before</p>
                        <DiffLines
                            lines={allLines.filter((l) => l.kind !== "add")}
                            testId="audit-diff-before"
                        />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">After</p>
                        <DiffLines
                            lines={allLines.filter((l) => l.kind !== "remove")}
                            testId="audit-diff-after"
                        />
                    </div>
                </div>
            ) : changeLines.length > 0 ? (
                <DiffLines lines={changeLines} testId="audit-diff-changes" />
            ) : (
                <p className="text-xs italic text-muted-foreground">No field values changed.</p>
            )}
        </div>
    );
};
